/**
 * Manejo de dinero.
 *
 * REGLA NORMATIVA DEL PROYECTO (Req. 10.8, 10.9):
 *
 *   Todo importe se almacena como ENTERO DE CENTAVOS.
 *
 * Motivo: en punto flotante, 0.1 + 0.2 no da exactamente 0.3. Con cientos de
 * compras y producciones ese error se acumula y las ganancias del mes salen
 * desviadas sin explicacion posible. Con enteros el problema no existe.
 *
 * Corolario: el redondeo ocurre UNA SOLA VEZ, al final de la cadena de
 * calculo. Redondear a medio camino desvia el resultado.
 */

import { ErrorValidacion } from './errors';

/** Importe en centavos. Siempre entero. `149900` equivale a $1,499.00 */
export type Centavos = number;

const CENTAVOS_POR_PESO = 100;

// ---------------------------------------------------------------------------
// Validacion
// ---------------------------------------------------------------------------

/** Verifica que un valor sea un importe valido en centavos. */
export function esCentavos(valor: unknown): valor is Centavos {
  return typeof valor === 'number' && Number.isInteger(valor) && Number.isFinite(valor);
}

/** Exige un importe valido en centavos o lanza un error de validacion. */
export function exigirCentavos(valor: unknown, campo = 'importe'): Centavos {
  if (!esCentavos(valor)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser un importe valido en centavos enteros.`,
      campo,
    );
  }
  return valor;
}

/** Exige un importe valido y no negativo. */
export function exigirCentavosNoNegativos(valor: unknown, campo = 'importe'): Centavos {
  const centavos = exigirCentavos(valor, campo);
  if (centavos < 0) {
    throw new ErrorValidacion(`El campo "${campo}" no puede ser negativo.`, campo);
  }
  return centavos;
}

// ---------------------------------------------------------------------------
// Conversion entre pesos y centavos
// ---------------------------------------------------------------------------

const PATRON_DECIMAL = /^(-)?(\d*)(?:\.(\d*))?$/;

/**
 * Convierte una cadena decimal a centavos SIN pasar por punto flotante.
 *
 * Multiplicar por 100 y redondear no basta: `1.005 * 100` da 100.49999999999999
 * porque el literal 1.005 no existe exacto en binario, y el redondeo devuelve
 * 100 en lugar de 101. Ese es justo el problema que la regla de centavos
 * enteros pretende eliminar, colandose por la puerta de entrada.
 *
 * Aqui se leen los digitos como texto y la aritmetica es entera de principio a
 * fin, asi que el resultado es el que dicta la notacion decimal.
 *
 * Redondeo: mitad hacia arriba en magnitud (media alza se aleja del cero), que
 * es la convencion contable habitual.
 */
function decimalACentavos(texto: string): Centavos | null {
  const coincidencia = PATRON_DECIMAL.exec(texto);
  if (coincidencia === null) return null;

  const signo = coincidencia[1] === '-' ? -1 : 1;
  const enteros = coincidencia[2] ?? '';
  const decimales = coincidencia[3] ?? '';
  if (enteros === '' && decimales === '') return null;

  // Mas de 15 digitos enteros excede la precision de un numero de JavaScript.
  // No ocurre con importes de un negocio, pero se rechaza en lugar de mentir.
  if (enteros.length > 15) return null;

  const parteEntera = enteros === '' ? 0 : Number(enteros);
  const centesimas = Number((decimales + '00').slice(0, 2));
  const siguienteDigito = Number((decimales + '000').charAt(2));

  const magnitud = parteEntera * CENTAVOS_POR_PESO + centesimas + (siguienteDigito >= 5 ? 1 : 0);
  return signo * magnitud;
}

/**
 * Convierte pesos a centavos.
 *
 * `String(pesos)` devuelve la representacion decimal mas corta que reproduce el
 * numero, lo que recupera la intencion decimal de quien lo escribio. A partir
 * de ahi la conversion es aritmetica entera.
 *
 * `pesosACentavos(1499)`     da `149900`
 * `pesosACentavos(124.9166)` da `12492`
 * `pesosACentavos(1.005)`    da `101`     (media alza, no 100)
 */
export function pesosACentavos(pesos: number): Centavos {
  if (!Number.isFinite(pesos)) {
    throw new ErrorValidacion('El importe capturado no es un numero valido.');
  }

  const texto = String(pesos);

  // La notacion exponencial (1e-7, 1e+21) queda fuera del rango de importes de
  // un negocio; se resuelve con la via directa.
  if (texto.includes('e') || texto.includes('E')) {
    return Math.round(pesos * CENTAVOS_POR_PESO);
  }

  const centavos = decimalACentavos(texto);
  if (centavos === null) {
    throw new ErrorValidacion('El importe capturado no es un numero valido.');
  }
  return centavos;
}

/**
 * Convierte centavos a pesos. Solo para presentacion o para capturar el valor
 * en un campo de formulario. NUNCA para seguir calculando.
 */
export function centavosAPesos(centavos: Centavos): number {
  return centavos / CENTAVOS_POR_PESO;
}

/**
 * Interpreta lo que la usuaria escribio en un campo de importe.
 * Acepta separadores de miles y el signo de pesos.
 * Devuelve null si el texto no representa un numero.
 */
export function textoACentavos(texto: string): Centavos | null {
  const limpio = texto.replace(/[$\s,\u00a0]/g, '').trim();
  if (limpio === '' || limpio === '-') return null;
  // Se parsea el decimal directamente, sin punto flotante intermedio.
  return decimalACentavos(limpio);
}

// ---------------------------------------------------------------------------
// Presentacion
// ---------------------------------------------------------------------------

const FORMATO_MONEDA = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formatea un importe para mostrarlo. Este es el ultimo eslabon de la cadena
 * de calculo: aqui, y solo aqui, el numero se vuelve texto.
 *
 * `formatearCentavos(149900)` da `"$1,499.00"`
 */
export function formatearCentavos(centavos: Centavos): string {
  // Se normalizan los espacios que algunas versiones de ICU insertan tras el
  // signo de moneda, para que la presentacion sea estable.
  return FORMATO_MONEDA.format(centavosAPesos(centavos)).replace(/\u00a0|\u202f/g, ' ').trim();
}

/** Formatea sin decimales cuando el importe es exacto. Util en resumenes. */
export function formatearCentavosCompacto(centavos: Centavos): string {
  if (centavos % CENTAVOS_POR_PESO === 0) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(centavosAPesos(centavos))
      .replace(/\u00a0|\u202f/g, ' ')
      .trim();
  }
  return formatearCentavos(centavos);
}

// ---------------------------------------------------------------------------
// Aritmetica
// ---------------------------------------------------------------------------

/**
 * Multiplica un importe por un factor y redondea a centavo entero.
 *
 * Se usa cuando el factor es la ultima operacion de la cadena, por ejemplo al
 * escalar una receta o prorratear un costo.
 */
export function multiplicarCentavos(centavos: Centavos, factor: number): Centavos {
  if (!Number.isFinite(factor)) {
    throw new ErrorValidacion('El factor de multiplicacion no es un numero valido.');
  }
  return Math.round(centavos * factor);
}

/**
 * Divide un importe en partes iguales y redondea a centavo entero.
 * Para costo por porcion.
 */
export function dividirCentavos(centavos: Centavos, divisor: number): Centavos {
  if (!Number.isFinite(divisor) || divisor === 0) {
    throw new ErrorValidacion('El divisor debe ser un numero distinto de cero.');
  }
  return Math.round(centavos / divisor);
}

/**
 * Reparte un importe en proporcion a unos pesos dados, garantizando que las
 * partes sumen EXACTAMENTE el total.
 *
 * Usa el metodo del residuo mayor: reparte la parte entera y luego asigna los
 * centavos sobrantes a quienes tengan la fraccion mas alta. Asi no quedan
 * centavos huerfanos, que es como aparecen los descuadres de un peso en los
 * reportes.
 *
 * `repartirCentavos(1000, [1, 1, 1])` da `[334, 333, 333]`
 */
export function repartirCentavos(total: Centavos, pesos: readonly number[]): Centavos[] {
  if (pesos.length === 0) return [];
  if (pesos.some((p) => !Number.isFinite(p) || p < 0)) {
    throw new ErrorValidacion('Los pesos del reparto deben ser numeros no negativos.');
  }

  const sumaPesos = pesos.reduce((a, b) => a + b, 0);
  if (sumaPesos === 0) {
    // Sin pesos que distingan, el total va completo al primer elemento.
    return pesos.map((_, i) => (i === 0 ? total : 0));
  }

  const exactos = pesos.map((p) => (total * p) / sumaPesos);
  const partes = exactos.map((v) => Math.floor(v));
  let restante = total - partes.reduce((a, b) => a + b, 0);

  const porFraccionDescendente = exactos
    .map((v, i) => ({ i, fraccion: v - Math.floor(v) }))
    .sort((a, b) => b.fraccion - a.fraccion);

  let cursor = 0;
  while (restante > 0 && porFraccionDescendente.length > 0) {
    const objetivo = porFraccionDescendente[cursor % porFraccionDescendente.length];
    if (objetivo !== undefined) {
      partes[objetivo.i] = (partes[objetivo.i] ?? 0) + 1;
      restante--;
    }
    cursor++;
  }

  return partes;
}

/**
 * Porcentaje que representa una parte respecto de un total.
 * Devuelve null cuando el total es cero, en lugar de un infinito o un cero
 * que aparentaria ser un dato real.
 */
export function porcentaje(parte: Centavos, total: Centavos): number | null {
  if (total === 0) return null;
  return (parte / total) * 100;
}

/** Formatea un porcentaje con un decimal. `71.333` da `"71.3 %"` */
export function formatearPorcentaje(valor: number | null): string {
  if (valor === null) return '—';
  return `${valor.toFixed(1).replace('-0.0', '0.0')} %`;
}
